import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NextStepsSection = () => {
  const nextSteps = [
    {
      id: 1,
      icon: "UserCheck",
      title: "Personal Consultation",
      description: "Our hydration expert will call you to understand your specific needs and preferences.",
      timeline: "Within 24 hours",
      color: "from-primary to-accent"
    },
    {
      id: 2,
      icon: "Package",
      title: "Custom Recommendations",
      description: "Receive personalized product suggestions based on your lifestyle and requirements.",
      timeline: "Within 48 hours",
      color: "from-accent to-secondary"
    },
    {
      id: 3,
      icon: "Truck",
      title: "Fast Delivery",
      description: "Once you place your order, enjoy free delivery across India within 3-5 business days.",
      timeline: "3-5 business days",
      color: "from-secondary to-primary"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOutBack"
      }
    }
  };

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-surface">
      <div className="max-w-6xl mx-auto">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-poppins font-bold text-primary mb-4">
            Your Mava Journey Begins
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Here's what happens next in your premium hydration experience
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {nextSteps?.map((step, index) => (
            <motion.div
              key={step?.id}
              className="relative group"
              variants={itemVariants}
            >
              <div className="bg-card border border-border rounded-xl p-6 h-full liquid-shadow hover:shadow-lg brand-transition">
                {/* Step Number */}
                <div className="absolute -top-4 -left-4 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-poppins font-bold text-sm">
                  {step?.id}
                </div>

                {/* Icon with Gradient Background */}
                <div className={`w-16 h-16 bg-gradient-to-br ${step?.color} rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 brand-transition`}>
                  <Icon name={step?.icon} size={28} color="white" strokeWidth={2} />
                </div>

                {/* Content */}
                <h3 className="text-xl font-poppins font-semibold text-primary mb-3">
                  {step?.title}
                </h3>
                
                <p className="text-text-primary mb-4 leading-relaxed">
                  {step?.description}
                </p>

                {/* Timeline Badge */}
                <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-3 py-1 rounded-full text-sm font-medium">
                  <Icon name="Clock" size={14} />
                  <span>{step?.timeline}</span>
                </div>

                {/* Connecting Line (except last item) */}
                {index < nextSteps?.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-border to-transparent transform -translate-y-1/2"></div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Call to Action */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          <div className="bg-card border border-border rounded-xl p-8 liquid-shadow max-w-2xl mx-auto">
            <h3 className="text-xl font-poppins font-semibold text-primary mb-4">
              Have Questions? We're Here to Help!
            </h3>
            <p className="text-text-secondary mb-6">
              Can't wait for our call? Reach out to us directly for immediate assistance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="outline"
                iconName="Phone"
                iconPosition="left"
                className="water-ripple"
              >
                Call +91 98765 43210
              </Button>
              <Button
                variant="default"
                iconName="MessageCircle"
                iconPosition="left"
                className="bg-action hover:bg-action/90 water-ripple"
              >
                WhatsApp Support
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default NextStepsSection;